import React, { useRef, useState } from 'react';

import { getLocation } from '$utilities/location';
import { useLocale } from '$contexts/locale.jsx';
import { IconChevronDown } from '$partials/icons';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const LocaleSelector = () => {
    const { current: locale, options: localeOptions, formatURI } = useLocale();
    const focusTargetRef = useRef();
    const location = getLocation();
    const buttonRef = useRef();

    const [isOpen, setIsOpen] = useState(false);
    const buttonMouseDownRef = useRef(false);

    const handleBlur = (e) => {
        if (isOpen) {
            const currentTarget = e.currentTarget;
            const wasButtonMouseDown = buttonMouseDownRef.current;
            buttonMouseDownRef.current = false;

            setTimeout(() => {
                if (!currentTarget.contains(document.activeElement) && document.activeElement !== buttonRef.current && !wasButtonMouseDown) {
                    setIsOpen(false);
                }
            }, 0);
        }
    };

    const open = () => {
        setIsOpen(prevState => {
            const newState = !prevState;
            if (newState) {
                setTimeout(() => focusTargetRef?.current?.focus(), 0);
            }
            return newState;
        });
    };

    return (
        <span className='flex items-center type-style-body-small mr-12 relative'>
            <button
                className='flex flex-row items-center align-center gap-10'
                onClick={open}
                onMouseDown={() => { buttonMouseDownRef.current = true; }}
                ref={buttonRef}
                type='button'>
                <span>
                    {locale?.labelShort}
                </span>
                <IconChevronDown
                    className={`w-[10px] transition-transform duration-100 ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <dialog
                className='absolute left-0 top-full pt-4 z-50 transition-all duration-150 bg-button-transparent-background'
                inert={isOpen}
                onBlur={handleBlur}
                open={isOpen}
                style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? 'auto' : 'none', transform: isOpen ? 'translateY(0)' : 'translateY(-4px)' }}>
                <ul className='bg-canvas border border-[var(--sand-100)] rounded shadow-[0px_4px_12px_5px_rgba(0,0,0,0.04)] w-[160px] p-4'>
                    {([...localeOptions].sort((a, b) => b.code === locale.code ? 1 : -1))?.map((option, i) => {
                        return (
                            <li key={i}>
                                <a
                                    className='flex items-center w-full min-h-[48px] px-8 py-8 rounded-[4px] type-style-body-small text-text-primary text-left hover:bg-[var(--sand-100)]'
                                    href={formatURI(location.pathname, option.code)}
                                    ref={i === 0 ? focusTargetRef : null}>
                                    {option.labelLong}
                                </a>
                            </li>
                        );
                    })}
                </ul>
            </dialog>
        </span>
    );
};


/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

export default LocaleSelector;
